import { useState, ChangeEvent, useMemo, useCallback } from 'react';
import { JobCountDetailResponse } from '@doda/api-client/aspida/@types';
import { findJobCount } from 'components/OccupationSelectModal/logics/createOccupationDisplayData';
import { OccupationDrillDownType } from 'pages/DodaFront/View/JobSearchList/components/SearchSideBar/DrillDown/OccupationDrillDown';
import { useAppSelector } from 'redux/hooks';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { getArrayFirst } from '@doda/common/src/logics/seoText/createSeoText';
import {
  OccupationDisplayListType,
  InitialOccupationDataType,
} from '../../types';

interface ArgumentType {
  initialOccupationData: InitialOccupationDataType;
  initialOccupationDrillDown?: OccupationDrillDownType | null;
}

/**
 * 職種に関するhooks
 * @param param.initialOccupationData 整形された職種マスタ
 * @param param.initialOccupationDrillDown ドリルダウン
 * @returns 表示用配列と更新処理
 */
export const useOccupation = ({
  initialOccupationData,
  initialOccupationDrillDown,
}: ArgumentType) => {
  const [occupationLList, setOccupationLList] = useState(
    initialOccupationData.lData ?? []
  );

  const [occupationMList, setOccupationMList] = useState(
    initialOccupationData.mData ?? []
  );

  const [occupationSList, setOccupationSList] = useState(
    initialOccupationData.sData ?? []
  );

  // 表示用配列
  const occupationDisplayList: OccupationDisplayListType[] = useMemo(() => {
    return occupationLList.map((occupation) => ({
      ...occupation,
      jobTypeMList: occupationMList
        .filter(({ parentId }) => parentId === occupation.searchJobTypeId)
        .map((occupationM) => ({
          ...occupationM,
          jobTypeSList: occupationSList.filter(
            ({ parentId }) => parentId === occupationM.searchJobTypeId
          ),
        })),
    }));
  }, [occupationLList, occupationMList, occupationSList]);

  // 選択中の大分類のid配列
  const selectedLIdList = useMemo(() => {
    return occupationLList
      .filter(({ isChecked }) => isChecked)
      .map(({ searchJobTypeId }) => searchJobTypeId);
  }, [occupationLList]);

  // 選択中の中分類のid配列
  const selectedMIdList = useMemo(() => {
    return occupationMList
      .filter(({ isChecked }) => isChecked)
      .map(({ searchJobTypeId }) => searchJobTypeId);
  }, [occupationMList]);

  // 選択中の小分類のid配列
  const selectedSIdList = useMemo(() => {
    return occupationSList
      .filter(({ isChecked }) => isChecked)
      .map(({ searchJobTypeId }) => searchJobTypeId);
  }, [occupationSList]);

  // 大分類のチェックボックス更新処理
  const updateIsCheckedOccupationLList = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      const checkedList = occupationLList.map((occupation) => {
        if (occupation.searchJobTypeId !== selectedId) return occupation;

        return {
          ...occupation,
          isChecked: event.target.checked,
        };
      });

      setOccupationLList(checkedList);

      return checkedList
        .filter(({ isChecked }) => isChecked)
        .map(({ searchJobTypeId }) => searchJobTypeId);
    },
    [occupationLList]
  );

  // 中分類のチェックボックス更新処理
  const updateIsCheckedOccupationMList = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      const checkedList = occupationMList.map((occupation) => {
        if (occupation.searchJobTypeId !== selectedId) return occupation;

        return {
          ...occupation,
          isChecked: event.target.checked,
        };
      });

      setOccupationMList(checkedList);

      return checkedList
        .filter(({ isChecked }) => isChecked)
        .map(({ searchJobTypeId }) => searchJobTypeId);
    },
    [occupationMList]
  );

  // 小分類のチェックボックス更新処理
  const updateIsCheckedOccupationSList = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      const checkedList = occupationSList.map((occupation) => {
        if (occupation.searchJobTypeId !== selectedId) return occupation;

        return {
          ...occupation,
          isChecked: event.target.checked,
        };
      });

      setOccupationSList(checkedList);

      return checkedList
        .filter(({ isChecked }) => isChecked)
        .map(({ searchJobTypeId }) => searchJobTypeId);
    },
    [occupationSList]
  );

  // 大分類の求人件数更新処理
  const updateLListJobCount = useCallback(
    (searchJobTypeIdList: JobCountDetailResponse[] | undefined) => {
      setOccupationLList((pre) =>
        pre.map((occupation) => {
          // チェックが入っていない場合は更新しない
          if (!occupation.isChecked) return occupation;

          return {
            ...occupation,
            jobCount: findJobCount(
              searchJobTypeIdList,
              occupation.searchJobTypeId
            ),
          };
        })
      );
    },
    []
  );

  // 中分類の求人件数更新処理
  const updateMListJobCount = useCallback(
    (
      searchJobTypeIdList: JobCountDetailResponse[] | undefined,
      parentId?: string | undefined
    ) => {
      setOccupationMList((pre) =>
        pre.map((occupation) => {
          /**
           * 中分類にチェックが入っていない場合、
           * 小分類にチェックが入っていない場合
           * ファセット件数を更新しない
           */
          const existCheckedSList = occupation.searchJobTypeId === parentId;
          if (!occupation.isChecked && !existCheckedSList) return occupation;

          return {
            ...occupation,
            jobCount: findJobCount(
              searchJobTypeIdList,
              occupation.searchJobTypeId
            ),
          };
        })
      );
    },
    []
  );

  // 小分類の求人件数更新処理
  const updateSListJobCount = useCallback(
    (
      searchJobTypeIdList: JobCountDetailResponse[] | undefined,
      searchJobTypeMIdList: JobCountDetailResponse[] | undefined
    ) => {
      setOccupationSList((pre) => {
        return pre.map((occupation) => {
          // チェックが入っていない場合は更新しない
          if (!occupation.isChecked) return occupation;

          updateMListJobCount(searchJobTypeMIdList, occupation.parentId);

          return {
            ...occupation,
            jobCount: findJobCount(
              searchJobTypeIdList,
              occupation.searchJobTypeId
            ),
          };
        });
      });
    },
    [updateMListJobCount]
  );

  // ドリルダウンで選択されている条件
  const { oc } = useAppSelector(appQueryParamsSelector);
  const currentLId = getArrayFirst(oc).replace('L', '');
  const currentMId = getArrayFirst(oc).replace('M', '');
  const currentSId = getArrayFirst(oc).replace('S', '');
  const selectedDrillDownLIdList =
    currentLId === initialOccupationDrillDown?.id
      ? [initialOccupationDrillDown?.id]
      : [];

  const nestedDrillDownList = initialOccupationDrillDown?.list ?? [];
  const selectedDrillDownMIdList = nestedDrillDownList
    .filter(({ id }) => id === currentMId)
    .map(({ id }) => id);

  const selectedDrillDownSIdList = nestedDrillDownList
    .filter(({ id }) => id === currentSId)
    .map(({ id }) => id);

  // 件数更新処理
  const updateOccupationJobCount = (
    lCount: JobCountDetailResponse[] | undefined,
    mCount: JobCountDetailResponse[] | undefined,
    sCount: JobCountDetailResponse[] | undefined,
    isToggleOccupation: boolean
  ) => {
    // 職種のチェックボックスを押下した際はファセット件数の更新処理をしない
    if (isToggleOccupation) return;
    updateLListJobCount(lCount);
    updateMListJobCount(mCount);
    updateSListJobCount(sCount, mCount);
  };

  return {
    occupationDisplayList,
    selectedOccupationLIdList: selectedDrillDownLIdList.length
      ? selectedDrillDownLIdList
      : selectedLIdList,
    selectedOccupationMIdList: selectedDrillDownMIdList.length
      ? selectedDrillDownMIdList
      : selectedMIdList,
    selectedOccupationSIdList: selectedDrillDownSIdList.length
      ? selectedDrillDownSIdList
      : selectedSIdList,
    occupationDrillDown: initialOccupationDrillDown,
    updateIsCheckedOccupationLList,
    updateIsCheckedOccupationMList,
    updateIsCheckedOccupationSList,
    updateOccupationJobCount,
  };
};
